import { useQuery } from "@tanstack/react-query";
import { Building, Loader2, Mail } from "lucide-react";
import { useSEO } from "@/hooks/use-seo";
import { PublicHeader } from "@/components/public-header";
import { PublicFooter } from "@/components/public-footer";

interface CertifiedPm {
  name: string;
  email: string;
  statesCovered: string[];
}

/**
 * Public directory of PadPulse Certified Property Managers — PMs with an
 * active CPM listing. Linked from the pricing page's "Want to be hands-off?"
 * section so owners who don't want to self-manage can find a vetted PM.
 */
export default function CertifiedPropertyManagersPage() {
  useSEO({
    tabTitle: "Certified Property Managers",
    title: "Certified Property Managers - PadPulse",
    description:
      "Want to be hands-off? Connect with a PadPulse Certified Property Manager — vetted co-living operators who run PadSplit portfolios on PadPulse.",
    path: "/certified-property-managers",
    // Certified PM listing is hidden (backend D24): unlinked and noindexed.
    noindex: true,
  });

  const pmsQuery = useQuery<CertifiedPm[]>({
    queryKey: ["/api/certified-property-managers"],
    staleTime: 60_000,
  });

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <PublicHeader />
      <div className="bg-gradient-to-br from-[#0a1628] via-[#0d2447] to-brand">
        <section className="pt-28 md:pt-32 pb-14 md:pb-16 text-center">
          <div className="container mx-auto px-6">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight" data-testid="text-cpm-heading">
              Certified Property Managers
            </h1>
            <p className="text-lg text-blue-100 max-w-2xl mx-auto">
              Want to be hands-off? These property managers run co-living portfolios on PadPulse and
              have earned the Certified PM listing. Reach out directly — they'd love to hear from you.
            </p>
          </div>
        </section>
      </div>

      <main className="flex-grow py-12 md:py-16">
        <div className="container mx-auto px-6 max-w-4xl">
          {pmsQuery.isLoading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-brand" />
            </div>
          ) : !pmsQuery.data || pmsQuery.data.length === 0 ? (
            <div className="text-center py-16 space-y-3" data-testid="text-cpm-empty">
              <Building size={40} className="mx-auto text-gray-300" />
              <h2 className="text-xl font-semibold text-gray-900">The directory is filling up</h2>
              <p className="text-gray-500 max-w-md mx-auto">
                Certified Property Managers are being onboarded now. Check back soon — or if you manage
                properties for owners, get listed from your PadPulse plan settings.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-gray-200 shadow-sm" data-testid="table-cpm-directory">
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-5 py-3 font-semibold">Property manager</th>
                    <th className="px-5 py-3 font-semibold">Contact</th>
                    <th className="px-5 py-3 font-semibold">States covered</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pmsQuery.data.map((pm) => (
                    <tr key={pm.email} className="hover:bg-gray-50" data-testid={`row-cpm-${pm.email}`}>
                      <td className="px-5 py-4 font-medium text-gray-900">{pm.name}</td>
                      <td className="px-5 py-4">
                        <a href={`mailto:${pm.email}`} className="inline-flex items-center gap-1.5 text-brand hover:underline">
                          <Mail size={14} className="shrink-0" /> {pm.email}
                        </a>
                      </td>
                      <td className="px-5 py-4 text-gray-600">
                        {pm.statesCovered.length > 0 ? pm.statesCovered.join(", ") : "Nationwide"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}
